import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { invalidateRecordAuthorization } from '../recordAuthorizationQuery'

const WORKFLOW_RESTRICTION_QUERY_KEY = 'GetWorkflowRestriction'

const workflowRestrictionQueryKey = (userId: string, workflowId: string) =>
  [WORKFLOW_RESTRICTION_QUERY_KEY, userId, workflowId] as const

/** Whether only the workflow's grantees may create requests from it. */
export const useWorkflowRestriction = (workflowId: string) => {
  const { getToken, user } = useAuthenticatedUser()
  return useQuery({
    queryKey: workflowRestrictionQueryKey(user.id, workflowId),
    queryFn: async () => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data, error } = await permissionsClient.GET('/entities/{entity}/{recordId}/restriction', {
        params: { path: { entity: 'workflow', recordId: workflowId } },
      })
      throwIfApiError(error)
      return data
    },
  })
}

/** Restricts a workflow to its grantees, or opens it again. */
export const useUpdateWorkflowRestriction = (workflowId: string) => {
  const { getToken, user } = useAuthenticatedUser()
  return useMutation({
    onMutate: async (_isRestricted, { client }) => {
      await client.cancelQueries({ queryKey: [WORKFLOW_RESTRICTION_QUERY_KEY] })
    },
    mutationFn: async (isRestricted: boolean) => {
      const authorization = await getToken()
      const permissionsClient = new PermissionsHttpClient({ authorization })
      const { data, error } = await permissionsClient.POST('/entities/{entity}/{recordId}/restriction', {
        params: { path: { entity: 'workflow', recordId: workflowId } },
        body: { isRestricted },
      })
      throwIfApiError(error)
      return data
    },
    // A re-read that fails after a successful write would otherwise leave the value read before it.
    onSuccess: (data, _isRestricted, _onMutateResult, { client }) => {
      client.setQueryData(workflowRestrictionQueryKey(user.id, workflowId), data)
    },
    onSettled: (_data, _error, _isRestricted, _onMutateResult, { client }) =>
      Promise.all([
        client.invalidateQueries({ queryKey: [WORKFLOW_RESTRICTION_QUERY_KEY] }),
        invalidateRecordAuthorization(client),
      ]),
  })
}
